import React, { useState, useEffect, useMemo } from 'react';
import {
  promoRewardService,
  PromoCode,
  StudentRewardProfile,
  PromoRewardStats,
} from '../../services/promoRewardService';
import { SEED_UNIVERSITIES } from '../../constants/mockSeed';
import {
  Plus,
  Search,
  Copy,
  CheckCircle2,
  Trash2,
  RefreshCw,
  Users,
  TrendingUp,
  X,
  Check,
} from 'lucide-react';
import { AdminLoadMore } from './AdminLoadMore';
import './AdminPages.css';

const TagIcon: React.FC<{ size?: number; className?: string; style?: React.CSSProperties }> = ({ size = 18, className, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
    <path d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z" />
    <path d="M7 7h.01" />
  </svg>
);

const GiftIcon: React.FC<{ size?: number; className?: string; style?: React.CSSProperties }> = ({ size = 18, className, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
    <path d="M20 12v10H4V12" />
    <path d="M2 7h20v5H2z" />
    <path d="M12 22V7" />
    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
  </svg>
);

const AwardIcon: React.FC<{ size?: number; className?: string; style?: React.CSSProperties }> = ({ size = 18, className, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
    <circle cx="12" cy="8" r="6" />
    <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
  </svg>
);

export const AdminPromoAndRewards: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'PROMOS' | 'REWARDS'>('PROMOS');
  const [promos, setPromos] = useState<PromoCode[]>([]);
  const [rewards, setRewards] = useState<StudentRewardProfile[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [visiblePromosCount, setVisiblePromosCount] = useState<number>(15);
  const [visibleRewardsCount, setVisibleRewardsCount] = useState<number>(15);

  // Modals state
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showBonusModal, setShowBonusModal] = useState<boolean>(false);
  const [selectedStudentForBonus, setSelectedStudentForBonus] = useState<StudentRewardProfile | null>(null);
  const [bonusPoints, setBonusPoints] = useState<string>('100');
  const [bonusTrips, setBonusTrips] = useState<string>('1');
  const [bonusReason, setBonusReason] = useState<string>('مكافأة تميز وإشادة بسفير OTO');

  // New Promo Form State
  const [newCode, setNewCode] = useState<string>('');
  const [discountType, setDiscountType] = useState<'PERCENTAGE' | 'FIXED'>('PERCENTAGE');
  const [discountValue, setDiscountValue] = useState<string>('15');
  const [minSpend, setMinSpend] = useState<string>('0');
  const [maxUsage, setMaxUsage] = useState<string>('100');
  const [targetUni, setTargetUni] = useState<string>('ALL');
  const [endDate, setEndDate] = useState<string>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 2);
    return d.toISOString().split('T')[0];
  });
  const [promoNotes, setPromoNotes] = useState<string>('');
  const [formError, setFormError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [promosList, rewardsList] = await Promise.all([
        promoRewardService.getAllPromoCodes(),
        promoRewardService.getAllStudentRewards(),
      ]);
      setPromos(promosList);
      setRewards(rewardsList);
    } catch (err) {
      console.warn('Error loading promo and rewards data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const stats: PromoRewardStats = useMemo(() => {
    return promoRewardService.calculateStats(promos, rewards);
  }, [promos, rewards]);

  // Copy code helper
  const handleCopy = (code: string) => {
    navigator.clipboard?.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2500);
  };

  // Toggle active status
  const handleTogglePromo = async (p: PromoCode) => {
    const newStatus = !p.isActive;
    await promoRewardService.togglePromoCodeStatus(p.id, newStatus);
    setPromos((prev) => prev.map((item) => (item.id === p.id ? { ...item, isActive: newStatus } : item)));
    setActionSuccess(`تم ${newStatus ? 'تفعيل' : 'إيقاف'} البروموكود ${p.code} بنجاح.`);
    setTimeout(() => setActionSuccess(null), 3000);
  };

  // Delete promo
  const handleDeletePromo = async (p: PromoCode) => {
    if (!window.confirm(`هل أنت متأكد من حذف البروموكود (${p.code}) نهائياً؟`)) return;
    await promoRewardService.deletePromoCode(p.id);
    setPromos((prev) => prev.filter((item) => item.id !== p.id));
    setActionSuccess(`تم حذف البروموكود (${p.code}) بنجاح.`);
    setTimeout(() => setActionSuccess(null), 3000);
  };

  // Create promo submit
  const handleCreatePromoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const codeClean = newCode.trim().toUpperCase();
    if (!codeClean || codeClean.length < 3) {
      setFormError('يرجى إدخال كود خصم صحيح (3 أحرف أو أرقام على الأقل)');
      return;
    }

    const val = parseFloat(discountValue);
    if (isNaN(val) || val <= 0) {
      setFormError('يرجى إدخال قيمة خصم صالحة أكبر من الصفر');
      return;
    }

    if (discountType === 'PERCENTAGE' && val > 100) {
      setFormError('نسبة الخصم المئوية لا يمكن أن تتجاوز 100%');
      return;
    }

    try {
      const created = await promoRewardService.createPromoCode({
        code: codeClean,
        discountType,
        discountValue: val,
        minSpend: parseFloat(minSpend) || 0,
        maxUsageCount: parseInt(maxUsage) || 100,
        applicableUniversities: targetUni === 'ALL' ? ['ALL'] : [targetUni],
        startDate: new Date().toISOString().split('T')[0],
        endDate,
        isActive: true,
        notes: promoNotes.trim(),
        createdBy: 'مدير النظام',
      });

      setPromos((prev) => [created, ...prev]);
      setShowCreateModal(false);
      setNewCode('');
      setPromoNotes('');
      setActionSuccess(`تم إنشاء وتفعيل كود الخصم (${created.code}) بنجاح!`);
      setTimeout(() => setActionSuccess(null), 4000);
    } catch (err: any) {
      setFormError(err?.message || 'تعذر إنشاء كود الخصم');
    }
  };

  // Grant bonus submit
  const handleGrantBonusSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudentForBonus) return;

    const pointsNum = parseInt(bonusPoints) || 0;
    const tripsNum = parseInt(bonusTrips) || 0;

    await promoRewardService.grantStudentBonus(selectedStudentForBonus.studentId, {
      points: pointsNum,
      freeTrips: tripsNum,
      reason: bonusReason,
    });

    setRewards((prev) =>
      prev.map((r) =>
        r.studentId === selectedStudentForBonus.studentId
          ? {
              ...r,
              pointsBalance: r.pointsBalance + pointsNum,
              freeTripsBalance: r.freeTripsBalance + tripsNum,
            }
          : r
      )
    );

    setShowBonusModal(false);
    setActionSuccess(`تم منح المكافأة للطالب (${selectedStudentForBonus.studentName}) بنجاح!`);
    setTimeout(() => setActionSuccess(null), 4000);
  };

  // Filtered promos
  const filteredPromos = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) return promos;
    return promos.filter(
      (p) =>
        p.code.toLowerCase().includes(q) ||
        (p.notes && p.notes.toLowerCase().includes(q))
    );
  }, [promos, searchTerm]);

  // Filtered rewards
  const filteredRewards = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) return rewards;
    return rewards.filter(
      (r) =>
        r.studentName.toLowerCase().includes(q) ||
        r.referralCode.toLowerCase().includes(q) ||
        r.phoneNumber.includes(q) ||
        r.universityName.toLowerCase().includes(q)
    );
  }, [rewards, searchTerm]);

  // Reset pagination on filter or tab change
  useEffect(() => {
    setVisiblePromosCount(15);
  }, [searchTerm, activeTab]);

  useEffect(() => {
    setVisibleRewardsCount(15);
  }, [searchTerm, activeTab]);

  const visiblePromos = useMemo(() => {
    return filteredPromos.slice(0, visiblePromosCount);
  }, [filteredPromos, visiblePromosCount]);

  const visibleRewards = useMemo(() => {
    return filteredRewards.slice(0, visibleRewardsCount);
  }, [filteredRewards, visibleRewardsCount]);


  const generateRandomCode = () => {
    const prefixes = ['OTO', 'CAMPUS', 'STUDENT', 'SUMMER', 'VIP', 'EXAM'];
    const p = prefixes[Math.floor(Math.random() * prefixes.length)];
    const num = Math.floor(10 + Math.random() * 90);
    setNewCode(`${p}${num}`);
  };

  return (
    <div className="admin-page-container" dir="rtl" style={{ padding: '1.5rem' }}>
      {/* 1. Header */}
      <div className="admin-page-header" style={{ marginBottom: '1.5rem' }}>
        <div className="admin-header-title-box">
          <h2>
            <GiftIcon size={26} className="text-[#E5232A]" />
            <span>إدارة البروموكود ومكافآت الطلاب وسفراء OTO</span>
          </h2>
          <p>
            توليد ومتابعة أكواد الخصم والترويج، احتساب مكافآت ونقاط سفراء الجامعات والاشتراكات المجانية تلقائياً.
          </p>
        </div>

        <div className="admin-header-actions-row">
          <button
            type="button"
            className="btn-primary flex items-center gap-2"
            onClick={() => {
              generateRandomCode();
              setShowCreateModal(true);
            }}
          >
            <Plus size={16} />
            <span>إنشاء كود خصم جديد</span>
          </button>

          <button
            type="button"
            className="btn-admin-outline flex items-center gap-2"
            onClick={loadData}
            disabled={isLoading}
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            <span>تحديث</span>
          </button>
        </div>
      </div>

      {/* Action Success Alert */}
      {actionSuccess && (
        <div className="p-3 mb-4 rounded-xl bg-emerald-50 border border-emerald-300 text-emerald-800 text-sm font-bold flex items-center gap-2">
          <CheckCircle2 size={18} />
          <span>{actionSuccess}</span>
        </div>
      )}

      {/* 2. KPI Metrics Grid */}
      <div className="admin-stats-strip" style={{ marginBottom: '1.75rem' }}>
        <div className="admin-stat-card">
          <div className="stat-icon red">
            <TagIcon size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.activePromoCodes}</span>
            <span className="stat-lbl">أكواد خصم نشطة</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon green">
            <TrendingUp size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.totalUsages}</span>
            <span className="stat-lbl">إجمالي الاستخدامات</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon amber">
            <Users size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{rewards.length}</span>
            <span className="stat-lbl">سفراء وطلاب مسجلين</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon blue">
            <AwardIcon size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.totalPointsGranted}</span>
            <span className="stat-lbl">رصيد نقاط المكافآت الممنوحة</span>
          </div>
        </div>
      </div>

      {/* 3. Tabs Navigation */}
      <div className="admin-tabs-bar" style={{ marginBottom: '1.5rem' }}>
        <button
          className={`admin-tab-btn ${activeTab === 'PROMOS' ? 'active' : ''}`}
          onClick={() => setActiveTab('PROMOS')}
        >
          <TagIcon size={16} />
          <span>أكواد الخصم والبروموكود (Promo Codes)</span>
          <span className="tab-counter-pill">{promos.length}</span>
        </button>

        <button
          className={`admin-tab-btn ${activeTab === 'REWARDS' ? 'active' : ''}`}
          onClick={() => setActiveTab('REWARDS')}
        >
          <AwardIcon size={16} />
          <span>سفراء الطلاب ومكافآت الإحالة (Ambassadors & Points)</span>
          <span className="tab-counter-pill green">{rewards.length}</span>
        </button>
      </div>

      {/* Search Input */}
      <div className="admin-filters-bar" style={{ marginBottom: '1.5rem' }}>
        <div className="admin-search-box" style={{ flex: 1 }}>
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder={
              activeTab === 'PROMOS'
                ? 'ابحث باسم أو كود الخصم...'
                : 'ابحث باسم الطالب، رقم الهاتف، أو كود الإحالة...'
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-btn" onClick={() => setSearchTerm('')}>
              ✕
            </button>
          )}
        </div>
      </div>

      {/* 4. Tab 1: Promo Codes Grid */}
      {activeTab === 'PROMOS' && (
        <div className="admin-table-card">
          {filteredPromos.length === 0 ? (
            <div className="admin-empty-state" style={{ padding: '3.5rem 1rem' }}>
              <TagIcon size={48} className="empty-icon text-slate-300" style={{ margin: '0 auto 1rem' }} />
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#334155' }}>لا توجد أكواد خصم حالياً</h3>
              <p style={{ color: '#64748B', fontSize: '0.875rem' }}>
                اضغط على زر "إنشاء كود خصم جديد" لإضافة كود تخفيض لطلابك فوراً.
              </p>
            </div>
          ) : (
            <div className="admin-table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>كود الخصم</th>
                    <th>نوع وقيمة الخصم</th>
                    <th>الاستخدامات المتبقية</th>
                    <th>الحد الأدنى للطلب</th>
                    <th>الجامعات المشمولة</th>
                    <th>تاريخ الانتهاء</th>
                    <th>الحالة</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePromos.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <div className="flex items-center gap-2">
                          <span
                            className="font-mono font-bold px-2.5 py-1 rounded-lg border text-sm"
                            style={{
                              backgroundColor: p.isActive ? '#FEF2F2' : '#F1F5F9',
                              color: p.isActive ? '#E5232A' : '#64748B',
                              borderColor: p.isActive ? '#FECDD3' : '#CBD5E1',
                            }}
                          >
                            {p.code}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleCopy(p.code)}
                            title="نسخ الكود"
                            className="p-1 rounded hover:bg-slate-100 text-slate-500"
                          >
                            {copiedCode === p.code ? (
                              <Check size={14} className="text-emerald-600" />
                            ) : (
                              <Copy size={14} />
                            )}
                          </button>
                        </div>
                      </td>

                      <td>
                        <span className="font-bold text-slate-800">
                          {p.discountType === 'PERCENTAGE'
                            ? `خصم ${p.discountValue}%`
                            : `خصم ${p.discountValue} ج.م`}
                        </span>
                      </td>

                      <td>
                        <div className="text-xs">
                          <span className="font-mono font-bold text-slate-700">
                            {p.currentUsageCount} / {p.maxUsageCount}
                          </span>
                          <div
                            className="w-24 bg-slate-100 h-1.5 rounded-full overflow-hidden mt-1"
                          >
                            <div
                              className="bg-[#E5232A] h-full"
                              style={{
                                width: `${Math.min(100, (p.currentUsageCount / p.maxUsageCount) * 100)}%`,
                              }}
                            />
                          </div>
                        </div>
                      </td>

                      <td>
                        <span className="text-xs text-slate-600">
                          {p.minSpend > 0 ? `${p.minSpend} ج.م` : 'بدون حد أدنى'}
                        </span>
                      </td>

                      <td>
                        <span className="text-xs text-slate-600">
                          {p.applicableUniversities.includes('ALL')
                            ? 'كافة الجامعات'
                            : p.applicableUniversities.join(', ')}
                        </span>
                      </td>

                      <td>
                        <span className="text-xs text-slate-500 font-mono">
                          {p.endDate}
                        </span>
                      </td>

                      <td>
                        <button
                          type="button"
                          onClick={() => handleTogglePromo(p)}
                          className={`text-xs px-2.5 py-1 rounded-full font-bold transition-colors ${
                            p.isActive
                              ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                              : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                          }`}
                        >
                          {p.isActive ? 'مفعل ✓' : 'معطل ✕'}
                        </button>
                      </td>

                      <td>
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleDeletePromo(p)}
                            className="p-1.5 text-rose-600 hover:bg-rose-50 rounded"
                            title="حذف الكود"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredPromos.length > 0 && (
                <AdminLoadMore
                  currentCount={Math.min(visiblePromosCount, filteredPromos.length)}
                  totalCount={filteredPromos.length}
                  itemName="بروموكود"
                  onLoadMore={() => setVisiblePromosCount((prev) => prev + 10)}
                  step={10}
                  onShowAll={() => setVisiblePromosCount(filteredPromos.length)}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* 5. Tab 2: Student Rewards & Ambassadors */}
      {activeTab === 'REWARDS' && (
        <div className="admin-table-card">
          {filteredRewards.length === 0 ? (
            <div className="admin-empty-state" style={{ padding: '3.5rem 1rem' }}>
              <AwardIcon size={48} className="empty-icon text-slate-300" style={{ margin: '0 auto 1rem' }} />
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#334155' }}>لا توجد بيانات طلاب حالياً</h3>
              <p style={{ color: '#64748B', fontSize: '0.875rem' }}>
                سيظهر الطلاب تلقائياً هنا عند تسجيل حساباتهم ومشاركتهم لأكواد الإحالة.
              </p>
            </div>
          ) : (
            <div className="admin-table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>الطالب والجامعة</th>
                    <th>كود الإحالة الخاص</th>
                    <th>الأصدقاء المدعوون</th>
                    <th>الحجوزات الفعلية</th>
                    <th>رصيد النقاط</th>
                    <th>الرحلات المجانية</th>
                    <th>المستوى التقديري</th>
                    <th>إجراءات الإدارة</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRewards.map((r) => (
                    <tr key={r.studentId}>
                      <td>
                        <div>
                          <strong className="block text-slate-800 text-sm">{r.studentName}</strong>
                          <span className="text-xs text-slate-500 font-mono block">{r.phoneNumber}</span>
                          <span className="text-xs text-[#E5232A] font-semibold">{r.universityName}</span>
                        </div>
                      </td>

                      <td>
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono font-bold text-xs bg-slate-100 text-slate-800 px-2 py-0.5 rounded border">
                            {r.referralCode}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleCopy(r.referralCode)}
                            title="نسخ كود الإحالة"
                            className="p-1 text-slate-400 hover:text-slate-700"
                          >
                            {copiedCode === r.referralCode ? (
                              <Check size={13} className="text-emerald-600" />
                            ) : (
                              <Copy size={13} />
                            )}
                          </button>
                        </div>
                      </td>

                      <td>
                        <span className="font-mono text-sm font-bold text-slate-700">
                          {r.totalInvited} طالب
                        </span>
                      </td>

                      <td>
                        <span className="font-mono text-sm font-bold text-emerald-700">
                          {r.activeBookings} حجز
                        </span>
                      </td>

                      <td>
                        <span className="font-mono font-bold text-sm text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                          {r.pointsBalance} نقطة
                        </span>
                      </td>

                      <td>
                        <span className="font-mono font-bold text-sm text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-200">
                          {r.freeTripsBalance} تذكرة
                        </span>
                      </td>

                      <td>
                        <span
                          className={`text-xs px-2.5 py-1 rounded-full font-bold ${
                            r.level === 'VIP'
                              ? 'bg-purple-100 text-purple-800'
                              : r.level === 'GOLD'
                              ? 'bg-amber-100 text-amber-800'
                              : r.level === 'SILVER'
                              ? 'bg-slate-200 text-slate-800'
                              : 'bg-emerald-50 text-emerald-700 border'
                          }`}
                        >
                          {r.level === 'VIP'
                            ? '👑 سفير VIP'
                            : r.level === 'GOLD'
                            ? '🥇 سفير ذهبي'
                            : r.level === 'SILVER'
                            ? '🥈 سفير فضي'
                            : '🥉 سفير برونزي'}
                        </span>
                      </td>

                      <td>
                        <button
                          type="button"
                          className="btn-admin-outline text-xs py-1 px-2.5 flex items-center gap-1 text-[#E5232A] border-[#FECDD3] hover:bg-rose-50"
                          onClick={() => {
                            setSelectedStudentForBonus(r);
                            setShowBonusModal(true);
                          }}
                        >
                          <GiftIcon size={13} />
                          <span>منح مكافأة</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredRewards.length > 0 && (
                <AdminLoadMore
                  currentCount={Math.min(visibleRewardsCount, filteredRewards.length)}
                  totalCount={filteredRewards.length}
                  itemName="طالب وسفير"
                  onLoadMore={() => setVisibleRewardsCount((prev) => prev + 10)}
                  step={10}
                  onShowAll={() => setVisibleRewardsCount(filteredRewards.length)}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* MODAL 1: Create Promo Code */}
      {showCreateModal && (
        <div className="admin-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div
            className="admin-modal-card"
            style={{ maxWidth: '520px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-modal-header">
              <div className="flex items-center gap-2">
                <TagIcon size={20} className="text-[#E5232A]" />
                <h3 className="font-bold text-base text-slate-900">إنشاء كود خصم ترويجي جديد</h3>
              </div>
              <button
                type="button"
                className="admin-modal-close-btn"
                onClick={() => setShowCreateModal(false)}
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreatePromoSubmit} className="admin-modal-body space-y-4">
              {formError && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs font-bold">
                  {formError}
                </div>
              )}

              {/* Code */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  كود الخصم (Promo Code)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                    placeholder="مثال: OTO2026 أو SUMMER15"
                    className="flex-1 font-mono uppercase font-bold text-base px-3 py-2 border rounded-xl outline-none focus:border-[#E5232A]"
                    required
                  />
                  <button
                    type="button"
                    onClick={generateRandomCode}
                    className="btn-admin-outline text-xs px-3"
                    title="توليد كود عشوائي"
                  >
                    توليد 🎲
                  </button>
                </div>
              </div>

              {/* Discount Type & Value */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">نوع الخصم</label>
                  <select
                    value={discountType}
                    onChange={(e) => setDiscountType(e.target.value as any)}
                    className="w-full text-xs font-bold px-3 py-2.5 border rounded-xl outline-none focus:border-[#E5232A]"
                  >
                    <option value="PERCENTAGE">نسبة مئوية (%)</option>
                    <option value="FIXED">مبلغ نقدي ثابت (ج.م)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    قيمة الخصم {discountType === 'PERCENTAGE' ? '(%)' : '(ج.م)'}
                  </label>
                  <input
                    type="number"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    min="1"
                    max={discountType === 'PERCENTAGE' ? 100 : 5000}
                    className="w-full text-xs font-bold px-3 py-2.5 border rounded-xl outline-none focus:border-[#E5232A]"
                    required
                  />
                </div>
              </div>

              {/* Usage Limit & Expiry Date */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    الحد الأقصى للاستخدامات
                  </label>
                  <input
                    type="number"
                    value={maxUsage}
                    onChange={(e) => setMaxUsage(e.target.value)}
                    min="1"
                    className="w-full text-xs px-3 py-2 border rounded-xl outline-none focus:border-[#E5232A]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    تاريخ انتهاء الصلاحية
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full text-xs px-3 py-2 border rounded-xl outline-none focus:border-[#E5232A]"
                    required
                  />
                </div>
              </div>

              {/* Min Spend & Target University */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    الحد الأدنى للطلب (ج.م)
                  </label>
                  <input
                    type="number"
                    value={minSpend}
                    onChange={(e) => setMinSpend(e.target.value)}
                    min="0"
                    placeholder="0 = بدون حد أدنى"
                    className="w-full text-xs px-3 py-2 border rounded-xl outline-none focus:border-[#E5232A]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    الجامعة المستهدفة
                  </label>
                  <select
                    value={targetUni}
                    onChange={(e) => setTargetUni(e.target.value)}
                    className="w-full text-xs px-3 py-2 border rounded-xl outline-none focus:border-[#E5232A]"
                  >
                    <option value="ALL">كافة الجامعات والكليات</option>
                    {SEED_UNIVERSITIES.map((u) => (
                      <option key={u.id} value={u.name}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  ملاحظات أو مناسبة العرض (اختياري)
                </label>
                <input
                  type="text"
                  value={promoNotes}
                  onChange={(e) => setPromoNotes(e.target.value)}
                  placeholder="مثال: خصم بداية الفصل الدراسي، أو لطلاب كلية الهندسة"
                  className="w-full text-xs px-3 py-2 border rounded-xl outline-none focus:border-[#E5232A]"
                />
              </div>

              <div className="admin-modal-footer pt-3">
                <button
                  type="button"
                  className="btn-admin-outline text-xs py-2 px-4"
                  onClick={() => setShowCreateModal(false)}
                >
                  إلغاء
                </button>
                <button type="submit" className="btn-primary text-xs py-2 px-5 font-bold">
                  حفظ وتفعيل الكود
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: Grant Bonus to Student */}
      {showBonusModal && selectedStudentForBonus && (
        <div className="admin-modal-overlay" onClick={() => setShowBonusModal(false)}>
          <div
            className="admin-modal-card"
            style={{ maxWidth: '480px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-modal-header">
              <div className="flex items-center gap-2">
                <GiftIcon size={20} className="text-[#E5232A]" />
                <h3 className="font-bold text-base text-slate-900">
                  منح مكافأة للطالب: {selectedStudentForBonus.studentName}
                </h3>
              </div>
              <button
                type="button"
                className="admin-modal-close-btn"
                onClick={() => setShowBonusModal(false)}
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleGrantBonusSubmit} className="admin-modal-body space-y-4">
              <div className="bg-slate-50 p-3 rounded-xl border text-xs text-slate-600">
                <span>كود إحالة الطالب: </span>
                <strong className="font-mono text-slate-900">
                  {selectedStudentForBonus.referralCode}
                </strong>
                <span className="block mt-1">
                  الرصيد الحالي: {selectedStudentForBonus.pointsBalance} نقطة |{' '}
                  {selectedStudentForBonus.freeTripsBalance} رحلات مجانية
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    إضافة نقاط مكافآت
                  </label>
                  <input
                    type="number"
                    value={bonusPoints}
                    onChange={(e) => setBonusPoints(e.target.value)}
                    min="0"
                    step="50"
                    className="w-full text-xs font-bold px-3 py-2 border rounded-xl outline-none focus:border-[#E5232A]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    إضافة رحلات / تذاكر مجانية
                  </label>
                  <input
                    type="number"
                    value={bonusTrips}
                    onChange={(e) => setBonusTrips(e.target.value)}
                    min="0"
                    className="w-full text-xs font-bold px-3 py-2 border rounded-xl outline-none focus:border-[#E5232A]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  سبب منح المكافأة
                </label>
                <input
                  type="text"
                  value={bonusReason}
                  onChange={(e) => setBonusReason(e.target.value)}
                  placeholder="سبب المكافأة أو الإشادة"
                  className="w-full text-xs px-3 py-2 border rounded-xl outline-none focus:border-[#E5232A]"
                  required
                />
              </div>

              <div className="admin-modal-footer pt-3">
                <button
                  type="button"
                  className="btn-admin-outline text-xs py-2 px-4"
                  onClick={() => setShowBonusModal(false)}
                >
                  إلغاء
                </button>
                <button type="submit" className="btn-primary text-xs py-2 px-5 font-bold">
                  اعتماد ومنح المكافأة
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPromoAndRewards;
